// src/pages/TransactionsPage.js — unified view of invoices + tickets + sponsorships
// [txn-refunds v1]: 15-min filter persistence, a per-transaction detail modal
//   (View no longer jumps to the event/invoice), and a Super Admin Cardknox
//   refund action inside the modal.
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '../utils/api';
import toast from 'react-hot-toast';
import DataGrid from '../components/DataGrid';

const TYPES = [
  { value: 'all',         label: 'All transactions' },
  { value: 'invoice',     label: 'Invoices only' },
  { value: 'ticket',      label: 'Event tickets only' },
  { value: 'sponsorship', label: 'Sponsorships only' },
];

function isoDate(offsetDays = 0) {
  const d = new Date();
  d.setDate(d.getDate() + offsetDays);
  return d.toISOString().substring(0, 10);
}

function startOfMonth(offsetMonths = 0) {
  const d = new Date();
  d.setMonth(d.getMonth() + offsetMonths, 1);
  return d.toISOString().substring(0, 10);
}

function endOfMonth(offsetMonths = 0) {
  const d = new Date();
  d.setMonth(d.getMonth() + offsetMonths + 1, 0);
  return d.toISOString().substring(0, 10);
}

const QUICK_FILTERS = [
  { label: 'This month',  from: () => startOfMonth(0),   to: () => isoDate(0) },
  { label: 'Last month',  from: () => startOfMonth(-1),  to: () => endOfMonth(-1) },
  { label: 'Last 90 days', from: () => isoDate(-90),     to: () => isoDate(0) },
  { label: 'This year',   from: () => `${new Date().getFullYear()}-01-01`, to: () => isoDate(0) },
  { label: 'All time',    from: () => '',                to: () => '' },
];

// ─── Filter persistence (remember for 15 minutes, then auto-reset) ─────────────
const FILTERS_KEY = 'njjba_txn_filters';
const FILTERS_TTL_MS = 15 * 60 * 1000;

function defaultFilters() {
  return { from: startOfMonth(0), to: isoDate(0), type: 'all', search: '' };
}

function loadSavedFilters() {
  try {
    const raw = localStorage.getItem(FILTERS_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object' || !parsed.filters) return null;
    if (typeof parsed.savedAt !== 'number' || (Date.now() - parsed.savedAt) > FILTERS_TTL_MS) {
      localStorage.removeItem(FILTERS_KEY);
      return null;
    }
    const f = parsed.filters;
    return {
      from:   typeof f.from   === 'string' ? f.from   : '',
      to:     typeof f.to     === 'string' ? f.to     : '',
      type:   typeof f.type   === 'string' ? f.type   : 'all',
      search: typeof f.search === 'string' ? f.search : '',
    };
  } catch (_) {
    return null;
  }
}

function saveFilters(filters) {
  try {
    localStorage.setItem(FILTERS_KEY, JSON.stringify({ filters, savedAt: Date.now() }));
  } catch (_) { /* storage unavailable — non-fatal */ }
}

function money(n) {
  return `$${(parseFloat(n) || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

export default function TransactionsPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState(() => loadSavedFilters() || defaultFilters());
  const [searchInput, setSearchInput] = useState(() => (loadSavedFilters() || defaultFilters()).search);
  const [detailTarget, setDetailTarget] = useState(null); // { kind, sourceId }

  const load = () => {
    setLoading(true);
    const params = {};
    if (filters.from)   params.from = filters.from;
    if (filters.to)     params.to = filters.to;
    if (filters.type && filters.type !== 'all') params.type = filters.type;
    if (filters.search) params.search = filters.search;
    if (filters.method) params.method = filters.method; // [marker: fixes-oct21 v1]

    api.get('/transactions', { params })
      .then(res => setData(res.data))
      .catch(() => toast.error('Could not load transactions.'))
      .finally(() => setLoading(false));
  };

  useEffect(load, [filters]);

  // Persist filters so they survive leaving the page (15-min window, refreshed
  // on each change; older than that resets to the default This-month view).
  useEffect(() => { saveFilters(filters); }, [filters]);

  // Debounce search
  useEffect(() => {
    const t = setTimeout(() => setFilters(f => ({ ...f, search: searchInput })), 350);
    return () => clearTimeout(t);
  }, [searchInput]);

  const handleQuickFilter = (qf) => {
    setFilters(f => ({ ...f, from: qf.from(), to: qf.to() }));
  };

  const handleResetFilters = () => {
    const d = defaultFilters();
    setSearchInput('');
    setFilters(d);
  };

  const handleExport = () => {
    const params = new URLSearchParams();
    if (filters.from)   params.set('from', filters.from);
    if (filters.to)     params.set('to', filters.to);
    if (filters.type && filters.type !== 'all') params.set('type', filters.type);
    if (filters.search) params.set('search', filters.search);
    if (filters.method) params.set('method', filters.method);
    window.open(`/api/transactions/export.csv?${params}`, '_blank');
  };

  const openDetail = (t) => setDetailTarget({ kind: t.kind, sourceId: t.sourceId });
  const closeDetail = () => setDetailTarget(null);

  const transactions = data?.transactions || [];
  const summary = data?.summary || { count: 0, totalAmount: 0, byKind: {}, refundedTotal: 0 };

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Transactions</h1>
          <p className="page-subtitle">All money received through the portal — invoices, event tickets, and sponsorships.</p>
        </div>
        <button className="btn btn-secondary" onClick={handleExport}>📥 Export CSV</button>
      </div>

      {/* Summary cards */}
      <div style={summaryRow}>
        <SummaryCard
          label="Total collected"
          value={summary.totalAmount}
          count={summary.count}
          note={summary.refundedTotal > 0 ? `net of ${money(summary.refundedTotal)} refunded` : null}
          highlight
        />
        <SummaryCard label="Invoices"    value={summary.byKind.invoice || 0} />
        <SummaryCard label="Tickets"     value={summary.byKind.ticket || 0} />
        <SummaryCard label="Sponsorships" value={summary.byKind.sponsorship || 0} />
      </div>

      {/* Filters */}
      <div className="card" style={{ marginBottom: 16 }}>
        <div className="card-body" style={{ padding: 14 }}>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 12 }}>
            {QUICK_FILTERS.map(qf => (
              <button
                key={qf.label}
                className="btn btn-sm btn-secondary"
                onClick={() => handleQuickFilter(qf)}
              >
                {qf.label}
              </button>
            ))}
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'flex-end' }}>
            <div style={fieldGroup}>
              <label style={fieldLabel}>From</label>
              <input
                type="date"
                value={filters.from}
                onChange={e => setFilters(f => ({ ...f, from: e.target.value }))}
                style={inputStyle}
              />
            </div>
            <div style={fieldGroup}>
              <label style={fieldLabel}>To</label>
              <input
                type="date"
                value={filters.to}
                onChange={e => setFilters(f => ({ ...f, to: e.target.value }))}
                style={inputStyle}
              />
            </div>
            <div style={fieldGroup}>
              <label style={fieldLabel}>Type</label>
              <select
                value={filters.type}
                onChange={e => setFilters(f => ({ ...f, type: e.target.value }))}
                style={inputStyle}
              >
                {TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </div>
            <div style={fieldGroup}>
              <label style={fieldLabel}>Paid by</label>
              <select value={filters.method || ''} onChange={e => setFilters(f => ({ ...f, method: e.target.value }))} style={inputStyle}>
                <option value="">Any way</option>
                <option value="card">Credit card only</option>
              </select>
            </div>
            <div style={{ ...fieldGroup, flex: 1, minWidth: 200 }}>
              <label style={fieldLabel}>Search</label>
              <input
                type="text"
                value={searchInput}
                onChange={e => setSearchInput(e.target.value)}
                placeholder="Name, email, reference…"
                style={inputStyle}
              />
            </div>
            <button className="btn btn-sm btn-secondary" onClick={handleResetFilters} style={{ height: 38 }}>
              Reset
            </button>
          </div>
        </div>
      </div>

      {/* [marker: step1-templates-grids v1] Excel-style table */}
      <DataGrid
        gridKey="transactions"
        rows={transactions}
        loading={loading}
        onRowClick={openDetail}
        height="calc(100vh - 420px)"
        totals={['amount', 'refundedAmount']}
        emptyText="No transactions for this filter."
        columns={[
          { key: 'date', label: 'Date', width: 105, type: 'date' },
          { key: 'kind', label: 'Type', width: 110, text: t => ({ invoice: 'Invoice', ticket: 'Ticket', sponsorship: 'Sponsorship' }[t.kind] || t.kind),
            render: t => <TypeBadge kind={t.kind} /> },
          { key: 'reference', label: 'Reference', width: 130, render: t => <span style={{ fontFamily: 'monospace', fontSize: '0.82rem' }}>{t.reference || ''}</span> },
          { key: 'description', label: 'Description', width: 260 },
          { key: 'payerName', label: 'Payer', width: 170 },
          { key: 'payerEmail', label: 'Payer Email', width: 190, defaultHidden: true },
          /* [marker: card-details v1] */
          { key: 'cardName', label: 'Name on Card', width: 150 },
          { key: 'authCode', label: 'Auth Code', width: 95, render: t => t.authCode ? <span style={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>{t.authCode}</span> : '' },
          { key: 'cardRef', label: 'Card Ref #', width: 115, render: t => t.cardRef ? <span style={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>{t.cardRef}</span> : '' },
          { key: 'cardLast4', label: 'Card Last 4', width: 85, defaultHidden: true },
          { key: 'amount', label: 'Amount', width: 110, type: 'money', align: 'right' },
          { key: 'refundedAmount', label: 'Refunded', width: 100, type: 'money', align: 'right',
            get: t => (parseFloat(t.refundedAmount) > 0 ? t.refundedAmount : null) },
          { key: 'status', label: 'Status', width: 120, text: t => String(t.status || '').replace(/_/g, ' '),
            render: t => <StatusBadge status={t.status} /> },
        ]}
      />

      {detailTarget && (
        <TransactionDetailModal
          target={detailTarget}
          onClose={closeDetail}
          onChanged={load}
        />
      )}
    </div>
  );
}

// ─── Detail + refund modal ────────────────────────────────────────────────────
function TransactionDetailModal({ target, onClose, onChanged }) {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [refundAmount, setRefundAmount] = useState('');
  const [refundReason, setRefundReason] = useState('');
  const [confirming, setConfirming] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const fetchDetail = () => {
    setLoading(true);
    setLoadError('');
    api.get(`/transactions/detail/${target.kind}/${target.sourceId}`)
      .then(res => {
        setDetail(res.data);
        setRefundAmount(res.data.remainingRefundable > 0 ? String(res.data.remainingRefundable.toFixed(2)) : '');
      })
      .catch(() => setLoadError('Could not load this transaction.'))
      .finally(() => setLoading(false));
  };

  useEffect(fetchDetail, [target.kind, target.sourceId]);

  const submitRefund = () => {
    const amt = parseFloat(refundAmount);
    if (!(amt > 0)) { toast.error('Enter a refund amount greater than $0.'); return; }
    if (detail && amt > detail.remainingRefundable + 0.005) {
      toast.error(`Max refundable is ${money(detail.remainingRefundable)}.`);
      return;
    }
    setSubmitting(true);
    api.post(`/transactions/refund/${target.kind}/${target.sourceId}`, {
      amount: amt,
      reason: refundReason,
    })
      .then(res => {
        toast.success(`Refunded ${money(res.data.amountRefunded)} to the cardholder.`);
        setConfirming(false);
        setRefundReason('');
        fetchDetail();
        if (onChanged) onChanged();
      })
      .catch(err => {
        toast.error(err?.response?.data?.error || 'Refund failed.');
        setConfirming(false);
      })
      .finally(() => setSubmitting(false));
  };

  return (
    <div style={overlay} onClick={onClose}>
      <div style={modalCard} onClick={e => e.stopPropagation()}>
        <div style={modalHeader}>
          <div>
            <div style={{ fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: 0.4, color: 'var(--gray-500)', fontWeight: 700 }}>
              Transaction details
            </div>
            <div style={{ fontSize: '1.1rem', fontWeight: 700, marginTop: 2 }}>
              {detail?.reference || '—'}
            </div>
          </div>
          <button className="btn btn-sm btn-secondary" onClick={onClose}>Close</button>
        </div>

        <div style={{ padding: 18 }}>
          {loading ? (
            <div style={{ textAlign: 'center', padding: 24, color: 'var(--gray-500)' }}>Loading…</div>
          ) : loadError ? (
            <div style={{ textAlign: 'center', padding: 24, color: '#b91c1c' }}>{loadError}</div>
          ) : detail ? (
            <>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, marginBottom: 14 }}>
                <div>
                  <div style={{ fontWeight: 600 }}>{detail.description}</div>
                  <div style={{ fontSize: '0.85rem', color: 'var(--gray-600)', marginTop: 2 }}>{detail.payerName}</div>
                  {detail.payerEmail && (
                    <div style={{ fontSize: '0.8rem', color: 'var(--gray-500)' }}>{detail.payerEmail}</div>
                  )}
                </div>
                <StatusBadge status={detail.status} />
              </div>

              <div style={detailGrid}>
                <DetailRow label="Amount" value={money(detail.amount)} />
                <DetailRow label="Collected" value={money(detail.collected)} />
                {detail.refundedAmount > 0 && (
                  <DetailRow label="Refunded" value={`−${money(detail.refundedAmount)}`} valueColor="#b91c1c" />
                )}
                <DetailRow label="Date" value={detail.date ? new Date(detail.date).toLocaleString('en-US') : '—'} />
                <DetailRow label="Payment method" value={detail.paymentMethod || '—'} />
                {detail.cardknoxTransactionId && (
                  <DetailRow label="Cardknox ref" value={detail.cardknoxTransactionId} mono />
                )}
                {detail.fields && Object.entries(detail.fields).map(([k, v]) => (
                  <DetailRow key={k} label={k} value={formatFieldValue(v)} />
                ))}
              </div>

              {detail.linkPath && (
                <div style={{ marginTop: 14 }}>
                  <Link to={detail.linkPath} className="btn btn-sm btn-secondary">Open full record →</Link>
                </div>
              )}

              {/* Refund history */}
              {detail.refunds && detail.refunds.length > 0 && (
                <div style={{ marginTop: 18 }}>
                  <div style={sectionTitle}>Refund history</div>
                  {detail.refunds.map(r => (
                    <div key={r.id} style={{ fontSize: '0.82rem', color: 'var(--gray-700)', padding: '6px 0', borderTop: '1px solid var(--gray-100)' }}>
                      <span style={{ fontWeight: 600, color: '#b91c1c' }}>−{money(r.amount)}</span>
                      {r.createdAt && <span style={{ color: 'var(--gray-500)' }}> · {new Date(r.createdAt).toLocaleString('en-US')}</span>}
                      {r.refundTransactionId && <span style={{ color: 'var(--gray-400)', fontFamily: 'monospace' }}> · {r.refundTransactionId}</span>}
                      {r.reason && <div style={{ color: 'var(--gray-500)' }}>{r.reason}</div>}
                    </div>
                  ))}
                </div>
              )}

              {/* Refund action */}
              <div style={{ marginTop: 18, borderTop: '1px solid var(--gray-200)', paddingTop: 16 }}>
                <div style={sectionTitle}>Refund to card</div>
                {!detail.cardknoxTransactionId ? (
                  <div style={{ fontSize: '0.85rem', color: 'var(--gray-500)' }}>
                    This transaction wasn't paid by credit card, so there's nothing to refund through Cardknox.
                  </div>
                ) : detail.remainingRefundable <= 0 ? (
                  <div style={{ fontSize: '0.85rem', color: 'var(--gray-500)' }}>
                    Fully refunded — nothing left to refund.
                  </div>
                ) : (
                  <>
                    <div style={{ fontSize: '0.8rem', color: 'var(--gray-600)', marginBottom: 8 }}>
                      Up to <strong>{money(detail.remainingRefundable)}</strong> can be refunded to the cardholder.
                    </div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'flex-end' }}>
                      <div style={fieldGroup}>
                        <label style={fieldLabel}>Amount</label>
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          max={detail.remainingRefundable}
                          value={refundAmount}
                          disabled={submitting || confirming}
                          onChange={e => setRefundAmount(e.target.value)}
                          style={{ ...inputStyle, width: 130 }}
                        />
                      </div>
                      <div style={{ ...fieldGroup, flex: 1, minWidth: 180 }}>
                        <label style={fieldLabel}>Reason (optional)</label>
                        <input
                          type="text"
                          value={refundReason}
                          disabled={submitting || confirming}
                          onChange={e => setRefundReason(e.target.value)}
                          placeholder="e.g. event cancelled"
                          style={inputStyle}
                        />
                      </div>
                    </div>

                    {!confirming ? (
                      <button
                        className="btn btn-sm"
                        onClick={() => setConfirming(true)}
                        disabled={submitting || !(parseFloat(refundAmount) > 0)}
                        style={{ ...refundBtn, marginTop: 12 }}
                      >
                        Refund {parseFloat(refundAmount) > 0 ? money(refundAmount) : ''}
                      </button>
                    ) : (
                      <div style={{ marginTop: 12, background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, padding: 12 }}>
                        <div style={{ fontSize: '0.85rem', color: '#7f1d1d', marginBottom: 10 }}>
                          Send <strong>{money(refundAmount)}</strong> back to the cardholder via Cardknox? This can't be undone here.
                        </div>
                        <div style={{ display: 'flex', gap: 8 }}>
                          <button className="btn btn-sm" onClick={submitRefund} disabled={submitting} style={refundBtn}>
                            {submitting ? 'Processing…' : 'Confirm refund'}
                          </button>
                          <button className="btn btn-sm btn-secondary" onClick={() => setConfirming(false)} disabled={submitting}>
                            Cancel
                          </button>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            </>
          ) : null}
        </div>
      </div>
    </div>
  );
}

function formatFieldValue(v) {
  if (v == null || v === '') return '—';
  if (typeof v === 'number') return money(v);
  // numeric strings from pg (e.g. "45.00") → format as money, else raw
  if (typeof v === 'string' && /^-?\d+(\.\d+)?$/.test(v)) return money(v);
  return v;
}

function DetailRow({ label, value, mono, valueColor }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, padding: '5px 0' }}>
      <div style={{ fontSize: '0.8rem', color: 'var(--gray-500)' }}>{label}</div>
      <div style={{
        fontSize: '0.85rem', fontWeight: 600, textAlign: 'right',
        color: valueColor || 'var(--gray-800)',
        fontFamily: mono ? 'monospace' : 'inherit',
        wordBreak: 'break-word',
      }}>{value}</div>
    </div>
  );
}

function SummaryCard({ label, value, count, note, highlight }) {
  return (
    <div style={{
      flex: 1, minWidth: 160, padding: 14,
      background: highlight ? '#1e3a5f' : '#fff',
      color: highlight ? '#fff' : 'inherit',
      border: '1px solid var(--gray-200)', borderRadius: 8,
    }}>
      <div style={{ fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: 0.3, opacity: 0.7, fontWeight: 700 }}>
        {label}
      </div>
      <div style={{ fontSize: '1.5rem', fontWeight: 700, marginTop: 4 }}>
        {money(value)}
      </div>
      {count != null && (
        <div style={{ fontSize: '0.75rem', opacity: 0.7, marginTop: 2 }}>{count} transactions</div>
      )}
      {note && (
        <div style={{ fontSize: '0.72rem', opacity: 0.85, marginTop: 2 }}>{note}</div>
      )}
    </div>
  );
}

function TypeBadge({ kind }) {
  const map = {
    invoice:     { bg: '#dbeafe', fg: '#1e40af', label: 'Invoice' },
    ticket:      { bg: '#ede9fe', fg: '#5b21b6', label: 'Ticket' },
    sponsorship: { bg: '#fef3c7', fg: '#92400e', label: 'Sponsorship' },
  };
  const s = map[kind] || map.ticket;
  return (
    <span style={{
      fontSize: '0.7rem', fontWeight: 700, padding: '2px 8px',
      background: s.bg, color: s.fg, borderRadius: 10, textTransform: 'uppercase',
    }}>{s.label}</span>
  );
}

function StatusBadge({ status }) {
  const s = String(status || '').toLowerCase();
  const styleMap = {
    paid:           { bg: '#dcfce7', fg: '#15803d' },
    comp:           { bg: '#dcfce7', fg: '#15803d' },
    refunded:       { bg: '#fee2e2', fg: '#b91c1c' },
    partial_refund: { bg: '#ffedd5', fg: '#9a3412' },
    sent:           { bg: '#e0e7ff', fg: '#3730a3' },
    pending:        { bg: '#fef9c3', fg: '#854d0e' },
    overdue:        { bg: '#fee2e2', fg: '#b91c1c' },
    draft:          { bg: 'var(--gray-100)', fg: 'var(--gray-600)' },
  };
  const c = styleMap[s] || { bg: '#dcfce7', fg: '#15803d' };
  return (
    <span style={{
      fontSize: '0.7rem', padding: '2px 8px',
      background: c.bg, color: c.fg,
      borderRadius: 10, fontWeight: 700, textTransform: 'uppercase',
    }}>{s.replace(/_/g, ' ') || '—'}</span>
  );
}

const summaryRow = { display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap' };
const fieldGroup = { display: 'flex', flexDirection: 'column' };
const fieldLabel = { fontSize: '0.75rem', fontWeight: 600, color: 'var(--gray-600)', marginBottom: 4, textTransform: 'uppercase', letterSpacing: 0.3 };
const inputStyle = { padding: '8px 12px', fontSize: '0.9rem', border: '1px solid var(--gray-300)', borderRadius: 6, background: '#fff', boxSizing: 'border-box' };
const th = { textAlign: 'left', padding: '12px 14px', fontSize: '0.75rem', fontWeight: 700, color: 'var(--gray-600)', textTransform: 'uppercase', letterSpacing: 0.3 };
const td = { padding: '10px 14px', fontSize: '0.9rem', color: 'var(--gray-800)', verticalAlign: 'top' };

const overlay = {
  position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.55)',
  display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
  padding: '40px 16px', zIndex: 1000, overflowY: 'auto',
};
const modalCard = {
  background: '#fff', borderRadius: 12, width: '100%', maxWidth: 560,
  boxShadow: '0 20px 60px rgba(0,0,0,0.3)', overflow: 'hidden',
};
const modalHeader = {
  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
  padding: '16px 18px', borderBottom: '1px solid var(--gray-200)',
};
const detailGrid = { borderTop: '1px solid var(--gray-100)' };
const sectionTitle = { fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: 0.3, fontWeight: 700, color: 'var(--gray-600)', marginBottom: 8 };
const refundBtn = { background: '#b91c1c', color: '#fff', border: 'none', fontWeight: 600 };
